// dialogs namespace - the generate/render/session modal bundle
// (components/modals/index.tsx) and the pack upload modal.
// Keys mirror pl/dialogs.ts exactly.
export const dialogs = {
  close: 'Close',

  // Leaving mid-upload (lib/useLeaveGuard.ts): the question is a plain
  // ConfirmDialog, so "Stay" holds the focus and Enter never throws work away.
  leaveGuard: {
    title: 'Stop this?',
    body: 'The upload is still going. If you leave, it starts over.',
    stay: 'Stay',
    leave: 'Leave',
  },

  confirmGenerate: {
    title: 'Generate {count} {edits}?',
    body: 'Each edit is built separately - different cuts and different clips, same music and same text style.',
    costLabel: 'Cost',
    afterLabel: 'Left after generating',
    refundNote: 'A failed render refunds your tokens automatically.',
    cancel: 'Cancel',
    confirm: 'Generate',
  },

  // R5 (rule 34 + principle 8, artboard 08c): title, a TABLE of Cost / You have, and
  // "Top up tokens". The sentence "You have 0, you need 3. Missing 3." is gone — the
  // third number was the first two subtracted, and the sentence said what the table shows.
  noGems: {
    title: 'Not enough tokens',
    // Mobile only: the store rules leave no top-up action there, so without this
    // sentence the table would be a dead end.
    hintNative: 'Make a smaller series - tokens are bought on the website.',
    back: 'Cancel',
    topUp: 'Top up tokens',
  },

  progress: {
    title: 'Building your edit…',
    body: 'We are cutting and stitching clips to the beat. You can close this window - the render keeps going.',
    background: 'Close, keep rendering',
    close: 'Close',
  },

  done: {
    title: 'Your edit is ready',
    body: 'The new edit is ready. Download it or open it in Edits.',
    bodySeries: 'New edit {n} is in the series “{series}”. Download it or open it in Edits.',
    download: 'Download MP4',
    library: 'Open Edits',
    edits: 'Open Edits',
  },

  deleteEdit: {
    title: 'Delete edit “{name}”?',
    body: 'The MP4 file ({size}) will be gone.',
    confirm: 'Delete edit',
  },

  failed: {
    title: 'The render did not finish',
    body: 'Something went wrong while building this edit. You lose nothing - the tokens are back in your balance.',
    statusLabel: 'Status',
    statusValue: 'Stopped',
    refundLabel: 'Tokens refunded',
    close: 'Close',
    retry: 'Try again',
  },

  sessionExpired: {
    title: 'Session expired',
    body: 'Sign in again to pick up where you left off. Your edits and tokens are safe.',
    login: 'Sign in again',
  },

  // The pack upload modal. It was 100% hardcoded Polish - an English user got a
  // Polish window in the middle of uploading their own files. The toggle label
  // comes from canvas 05 ("Cut into separate clips"): it says what will HAPPEN
  // instead of naming the technology.
  packUpload: {
    title: 'Upload clips to a pack',
    appendTitle: 'Add files to “{name}”',
    nameLabel: 'Pack name',
    namePlaceholder: 'e.g. Music video - winter',
    collectionLabel: 'Collection',
    collectionUnassigned: 'Unassigned',
    filesLabel: 'Files',
    dropStrong: 'Drop files here',
    dropRest: ' or click to choose',
    dropFormats: '.mp4 · .mov · .webm · .mkv',
    cutTitle: 'Cut into separate clips',
    removeFile: 'Remove {name}',
    fileError: 'error',
    fileDone: 'done',
    fileFinalizing: 'finishing…',
    fileStarting: 'starting…',
    cancel: 'Cancel',
    close: 'Close',
    submit: 'Upload',
    submitCount: 'Upload ({count})',
    appendSubmit: 'Add ({count})',
    createEmpty: 'Create empty pack',
    busy: 'Uploading…',
    appendBusy: 'Adding…',
    createBusy: 'Creating…',
    retryCollection: 'Try assigning again',
    finish: 'Finish',
    collectionError: 'The pack “{name}” was created. Its collection assignment could not be confirmed. Retry the assignment - the files will not be uploaded twice.',
    error: 'The operation could not be completed. Try again.',
    packBusy: 'This pack is being processed. Wait for it to finish and try again.',
    storageFull: 'Your library is out of space. Free some space before adding files.',
    notEnoughGems: 'You need more tokens to cut these files. Choose fewer files or turn off cutting.',
    confirmUpload: 'Finish upload',
    pendingFinalize: 'The files have been sent. Retry confirmation in this dialog - they will not be uploaded twice.',
    partialFailure: 'Some files were saved to the pack. Could not add: {files}. You can add them again later.',
    storageUnavailable: 'Your browser cannot save upload progress. Enable storage for this site and try again.',
    terminalFailure: 'This upload can no longer be completed. Check your library before adding the files again.',
    discardPending: 'Close and check library',
  },

  reRender: {
    title_one: 'Retry {n} edit?',
    title_few: 'Retry {n} edits?',
    title_many: 'Retry {n} edits?',
    // One sentence, the same for every entry point (a done edit, a failed one, a series
    // card). The old copy promised a refund in a window that usually opens from a DONE
    // edit, where nothing failed and nothing comes back.
    // R5 (principle 8): "Same music, same style." is gone — the window gets ONE
    // sentence, and the second one described what a retry does not change anyway.
    body: 'A new render draws the clips again.',
    costLabel: 'Cost',
    haveLabel: 'You have',
    leftLabel: 'Left',
    cancel: 'Cancel',
    // Verb only; `payLabel` appends the price ("Retry · 6 tokens").
    confirm: 'Retry',
  },
} as const
